/**
 * Document section data model for notebook-cell editor
 */

/**
 * Document type identifier.
 * Story 297: Widened from union to string for generic document support.
 * Use DOC_TYPE_PRD / DOC_TYPE_DESIGN constants for the fixed types.
 */
export type DocType = string;

/** Fixed document type: PRD */
export const DOC_TYPE_PRD = 'prd' as const;
/** Fixed document type: Tech Design */
export const DOC_TYPE_DESIGN = 'design' as const;

/**
 * Section type for document sections
 * Story 250: Design section types
 * Story 293: PRD section types for G1 gate control
 */
export type SectionType =
  | 'overview'         // Design: informational context
  | 'capability'       // Design: testable functionality (has anchor)
  | 'test_strategy'    // Design: testing approach
  | 'requirement'      // PRD: must be covered by design (G1 enforced)
  | 'informational';   // PRD: context only (excluded from G1)

/** Design-only section types */
export type DesignSectionType = 'overview' | 'capability' | 'test_strategy';

/** PRD-only section types */
export type PRDSectionType = 'requirement' | 'informational' | 'test_strategy';

/** Display labels for section types (Story 254, Story 293) */
export const SECTION_TYPE_LABELS: Record<SectionType, string> = {
  // Design types
  overview: 'Informational',
  capability: 'Capability',
  test_strategy: 'Testing',
  // PRD types (Story 293)
  requirement: 'Requirement',
  informational: 'Information Only',
} as const;

/** Tooltips for section type options in UI (Story 254, Story 293) */
export const SECTION_TYPE_TOOLTIPS: Record<SectionType, string> = {
  // Design types
  overview: 'Useful for context but no work planned against this section',
  capability: 'Testable functionality that tasks can implement',
  test_strategy: 'Contributes to how this story will be tested',
  // PRD types (Story 293)
  requirement: 'Must be covered by tech design (G1 gate enforced)',
  informational: 'Background context only (excluded from G1 gate)',
} as const;

/** Design-only labels for dropdown */
export const DESIGN_TYPE_LABELS: Record<DesignSectionType, string> = {
  overview: 'Informational',
  capability: 'Capability',
  test_strategy: 'Testing',
} as const;

/** PRD-only labels for dropdown */
export const PRD_TYPE_LABELS: Record<PRDSectionType, string> = {
  requirement: 'Requirement',
  informational: 'Information Only',
  test_strategy: 'Testing',
} as const;

/** Design-only tooltips */
export const DESIGN_TYPE_TOOLTIPS: Record<DesignSectionType, string> = {
  overview: 'Useful for context but no work planned against this section',
  capability: 'Testable functionality that tasks can implement',
  test_strategy: 'Contributes to how this story will be tested',
} as const;

/** PRD-only tooltips */
export const PRD_TYPE_TOOLTIPS: Record<PRDSectionType, string> = {
  requirement: 'Must be covered by tech design (G1 gate enforced)',
  informational: 'Background context only (excluded from G1 gate)',
  test_strategy: 'Contributes to how this story will be tested',
} as const;

export interface DocumentSection {
  id?: string;             // Internal section ID for history tracking (Story 232)
  section_key: string;     // Unique ID (e.g., 'problem', 'auth-flow')
  title: string;           // Display heading
  body: string;            // Markdown content
  seq: number;             // Order (1, 2, 3...)
  anchor?: string | null;  // Capability ID (e.g., 'auth.login') or PRD anchor (e.g., 'req.auth')
  covers?: string[] | null; // Design sections: list of PRD anchors this section covers
  summary?: string;        // Optional brief description
  section_type?: SectionType | null; // Section type (Story 250): overview, capability, test_strategy
  is_stale?: boolean;               // Story 429: upstream dependency changed since last review
}

/** Metadata for design document sections */
export interface DesignSectionMetadata {
  taskCount: number;
  tasks: Array<{ seq: number; title: string; status: string }>;
  requiredTests: string[];  // ['unit', 'integration', ...]
}

/** Metadata for PRD document sections */
export interface PRDSectionMetadata {
  coveredBy: Array<{ anchor: string; title: string }>;  // Design capabilities covering this requirement
}

/** Union type for section metadata based on doc type */
export type SectionMetadata = DesignSectionMetadata | PRDSectionMetadata;

/** Type guard to check if metadata is for design docs */
export function isDesignMetadata(
  metadata: SectionMetadata | undefined
): metadata is DesignSectionMetadata {
  return metadata !== undefined && 'taskCount' in metadata;
}

/** Type guard to check if metadata is for PRD docs */
export function isPRDMetadata(
  metadata: SectionMetadata | undefined
): metadata is PRDSectionMetadata {
  return metadata !== undefined && 'coveredBy' in metadata;
}

export interface EditorState {
  sections: DocumentSection[];
  activeSection: string | null;  // section_key of section being edited
  dirtySection: string | null;   // section_key with unsaved changes
  draggedSection: string | null; // section_key being dragged
}
